class AccountDropdown extends HTMLElement {
  constructor() {
    super();

    // Allow either explicit IDs or data-attributes inside the component
    this.trigger =
      this.querySelector('[data-dropdown-trigger]') ||
      this.querySelector('button[aria-haspopup="menu"]');

    this.menu =
      this.querySelector('[data-dropdown-menu]') ||
      (this.trigger?.getAttribute('aria-controls')
        ? document.getElementById(this.trigger.getAttribute('aria-controls'))
        : null);

    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);

    if (!this.trigger || !this.menu) return;

    // Initial a11y state
    this.trigger.setAttribute('aria-expanded', 'false');
    this.menu.hidden = true;

    // Events
    this.trigger.addEventListener('click', this.toggle.bind(this));
    this.trigger.addEventListener('keydown', (evt) => {
      // Open with Enter/Space/ArrowDown when focused on the trigger
      if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'ArrowDown') {
        evt.preventDefault();
        this.open();
      }
    });

    // Escape support when focus is inside the menu
    this.menu.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape') {
        evt.preventDefault();
        this.close();
      }
    });
  }

  open() {
    if (!this.menu.hidden) return;

    this.activeElement = document.activeElement;

    this.menu.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');

    // Focus first item for keyboard users
    const items = this.getItems();
    if (items[0]) items[0].focus();

    document.body.addEventListener('click', this.onBodyClick);
    document.addEventListener('keydown', this.onKeyDown);

    // Optional: if you have these utilities available (like in Shopify themes)
    if (typeof trapFocus === 'function') trapFocus(this.menu);
  }

  close({ returnFocus = true } = {}) {
    if (this.menu.hidden) return;

    this.menu.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');

    document.body.removeEventListener('click', this.onBodyClick);
    document.removeEventListener('keydown', this.onKeyDown);

    // Optional: if you have these utilities available
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);

    if (returnFocus) (this.activeElement || this.trigger).focus();
  }

  toggle() {
    this.menu.hidden ? this.open() : this.close({ returnFocus: false });
  }

  getItems() {
    // role="menuitem" is ideal, but also support plain links/buttons
    const candidates = this.menu.querySelectorAll(
      '[role="menuitem"], a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    return Array.from(candidates).filter((el) => {
      if (el.hasAttribute('disabled')) return false;
      if (el.getAttribute('aria-disabled') === 'true') return false;
      // Skip separators / non-focusables
      return typeof el.focus === 'function';
    });
  }

  handleBodyClick(evt) {
    const target = evt.target;

    // If click is outside this component, close
    if (target !== this && !target.closest('account-dropdown')) {
      this.activeElement = document.activeElement;
      this.close({ returnFocus: false });
    }
  }

  handleKeyDown(evt) {
    // Only handle keys when open and focus is within the component
    if (this.menu.hidden) return;

    const isWithin = this.contains(document.activeElement);
    if (!isWithin) return;

    const items = this.getItems();
    if (!items.length) return;

    const idx = items.indexOf(document.activeElement);

    if (evt.key === 'Escape') {
      evt.preventDefault();
      this.close();
      return;
    }

    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      const next = items[(idx + 1) % items.length] || items[0];
      next.focus();
      return;
    }

    if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      const prev = items[(idx - 1 + items.length) % items.length] || items[items.length - 1];
      prev.focus();
      return;
    }

    if (evt.key === 'Home') {
      evt.preventDefault();
      items[0].focus();
      return;
    }

    if (evt.key === 'End') {
      evt.preventDefault();
      items[items.length - 1].focus();
      return;
    }

    // Common UX: close when tabbing away from the menu
    if (evt.key === 'Tab') {
      this.close({ returnFocus: false });
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('account-dropdown', AccountDropdown);